import { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";

// Components
import { SideModal, CentralModal, Card } from "../UI";
import TiketDetails from "../TiketDetails/TiketDetails";
import TiketDetailsSkeleton from "../TiketDetails/TiketDetailsSkeleton";
import TiketDetailsError from "../TiketDetails/TiketDetailsError";

// Containers
import Chat from "../../containers/chat/Chat";
import WorkersForm from "../../containers/forms/workers/Workers.form";
import DeclineForm from "../../containers/forms/serviceRequests/DeclineForm/Decline.form";

// Constants
import * as modalTypes from "../../constants/serviceRequests.constants";

// Actions
import {
  closeModal as closeServicesModal,
  closeCentralModal,
  seeNewMessages as seeServiceMessages,
  setNewMessageToService
} from "../../redux/actions/services.actions";
import {
  closeModals as closeServiceRequestsModals,
  seeNewMessages as seeRequestMessages,
  setNewMessageToServiceRequest
} from "../../redux/actions/service-requests.actions";
import { setNewChatMessage } from "../../redux/actions/chat.actions";

// Services
import { enableScroll } from "../../utils";

/**
 * Global host for the service / service-request detail drawers (and their
 * child modals), mounted once in AppShell. Both drawers are driven purely by
 * redux state, so they open over ANY page — a notification click from the
 * Dashboard opens the drawer in place, no navigation involved.
 *
 * The blocks were moved here from Services.js, ServiceRequests.js and
 * Schedule.js (which used to each render their own copy).
 */
const GlobalServiceDrawers = () => {
  const dispatch = useDispatch();

  const {
    selectedService,
    modalType: servicesModalType,
    tiketDetailsModalType: servicesTiketDetailsModalType,
    centralModalType,
    workersFormData,
    isLoadingDetails: isLoadingServiceDetails,
    detailsError: serviceDetailsError
  } = useSelector(state => state.servicesReducer);

  const {
    selectedRequest,
    modalType: requestsModalType,
    tiketDetailsModalType: requestsTiketDetailsModalType,
    isLoadingDetails: isLoadingRequestDetails,
    detailsError: requestDetailsError
  } = useSelector(state => state.serviceRequestsPage);

  const { newMessage } = useSelector(state => state.chatReducer);

  // Chat open state is kept per drawer so closing one drawer can't leak an
  // expanded chat into the other.
  const [isServiceChatOpen, setIsServiceChatOpen] = useState(false);
  const [isRequestChatOpen, setIsRequestChatOpen] = useState(false);

  // New-chat-message badging, moved from the pages: bump the unread counter
  // on the matching list item / opened drawer unless a chat is expanded.
  // The bump actions are id-matched no-ops in the slice that doesn't hold
  // the service, so dispatching both is safe.
  useEffect(() => {
    if (!newMessage || isServiceChatOpen || isRequestChatOpen) return;
    const serviceId = parseInt(newMessage.serviceId, 10);
    dispatch(setNewMessageToService(serviceId));
    dispatch(setNewMessageToServiceRequest(serviceId));
    dispatch(setNewChatMessage(undefined));
  }, [newMessage, isServiceChatOpen, isRequestChatOpen, dispatch]);

  const anyGlobalModalOpen =
    servicesModalType === modalTypes.TIKET_DETAILS ||
    centralModalType === modalTypes.WORKERS ||
    requestsModalType === modalTypes.TIKET_DETAILS ||
    requestsModalType === modalTypes.DECLINE;

  // Reducer-driven closes (approve / decline / start / finish success) unmount
  // the drawer without going through a close handler, which used to leave the
  // page scroll-locked and the chat flag stale. Release both on the
  // open -> closed transition.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (wasOpenRef.current && !anyGlobalModalOpen) {
      enableScroll();
      setIsServiceChatOpen(false);
      setIsRequestChatOpen(false);
    }
    wasOpenRef.current = anyGlobalModalOpen;
  }, [anyGlobalModalOpen]);

  const closeServicesDrawer = () => {
    enableScroll();
    dispatch(closeServicesModal());
    setIsServiceChatOpen(false);
  };

  const closeRequestsDrawer = () => {
    enableScroll();
    dispatch(closeServiceRequestsModals());
    setIsRequestChatOpen(false);
  };

  const toggleServiceChat = serviceId => {
    dispatch(setNewChatMessage(undefined));
    dispatch(seeServiceMessages(serviceId));
    setIsServiceChatOpen(prevValue => !prevValue);
  };

  const toggleRequestChat = serviceId => {
    dispatch(setNewChatMessage(undefined));
    dispatch(seeRequestMessages(serviceId));
    setIsRequestChatOpen(prevValue => !prevValue);
  };

  return (
    <>
      {/* ——— Services drawer ——— */}
      {servicesModalType === modalTypes.TIKET_DETAILS && (
        <SideModal
          isChatOpen={isServiceChatOpen}
          closeModal={closeServicesDrawer}
        >
          {serviceDetailsError ? (
            <TiketDetailsError />
          ) : isLoadingServiceDetails || !selectedService ? (
            <TiketDetailsSkeleton />
          ) : (
            <TiketDetails
              serviceDetails={selectedService}
              tiketDetailsModalType={servicesTiketDetailsModalType}
              shouldShowWokers
              isServicesDrawer
              toggleChat={() => toggleServiceChat(selectedService.id)}
              closeModal={closeServicesDrawer}
            />
          )}
          {selectedService && (
            <Chat
              service={selectedService}
              isOpen={isServiceChatOpen}
              onClose={() => setIsServiceChatOpen(false)}
            />
          )}
        </SideModal>
      )}

      {/* Workers form — opened from the services drawer ("Edit" workers).
          No enableScroll on close: the drawer behind it is still open. */}
      {centralModalType === modalTypes.WORKERS && (
        <CentralModal
          width={440}
          closeModal={() => dispatch(closeCentralModal())}
        >
          <Card>
            <WorkersForm
              closeModal={() => dispatch(closeCentralModal())}
              workersFormData={workersFormData}
            />
          </Card>
        </CentralModal>
      )}

      {/* ——— Service-request drawer ——— */}
      {requestsModalType === modalTypes.TIKET_DETAILS && (
        <SideModal
          isChatOpen={isRequestChatOpen}
          closeModal={closeRequestsDrawer}
        >
          {requestDetailsError ? (
            <TiketDetailsError />
          ) : isLoadingRequestDetails || !selectedRequest ? (
            <TiketDetailsSkeleton />
          ) : (
            <TiketDetails
              serviceDetails={selectedRequest}
              tiketDetailsModalType={requestsTiketDetailsModalType}
              isServicesDrawer={false}
              toggleChat={() => toggleRequestChat(selectedRequest.id)}
              closeModal={closeRequestsDrawer}
            />
          )}
          {selectedRequest && (
            <Chat
              service={selectedRequest}
              isOpen={isRequestChatOpen}
              onClose={() => setIsRequestChatOpen(false)}
            />
          )}
        </SideModal>
      )}

      {/* Decline modal — reached from the SR list row button AND from any
          drawer's footer "Decline", so it has to live globally too. When the
          decline was triggered from the (still open) services drawer, keep
          the scroll lock on cancel — that drawer's own close releases it. */}
      {requestsModalType === modalTypes.DECLINE && (
        <CentralModal
          width={400}
          closeModal={() => {
            if (servicesModalType !== modalTypes.TIKET_DETAILS) enableScroll();
            dispatch(closeServiceRequestsModals());
          }}
        >
          <Card>
            <DeclineForm
              closeModal={() => {
                if (servicesModalType !== modalTypes.TIKET_DETAILS)
                  enableScroll();
                dispatch(closeServiceRequestsModals());
              }}
            />
          </Card>
        </CentralModal>
      )}
    </>
  );
};

export default GlobalServiceDrawers;
